/**
 * Calculate optimal polling intervals based on daily API limits
 * Ensures we stay well within daily limits with safety margins
 */

// API daily limits (from rateLimiter.js)
const DAILY_LIMITS = {
  morpho: 10000,
  coingecko: 5000,
  etherscan: 100000,
  coinglass: 500,
  defillama: 10000
};

// Safety margin percentage (use only this % of daily limit)
const SAFETY_MARGIN = 0.1; // Use 10% of daily limit - conservative but reasonable

// Minimum intervals (in milliseconds) - reasonable minimums
const MIN_INTERVALS = {
  morpho: 2 * 60 * 1000,      // 2 minutes minimum - utilization changes slowly
  coingecko: 2 * 60 * 1000,   // 2 minutes minimum - price can change but not constantly
  etherscan: 10 * 60 * 1000,  // 10 minutes minimum - loop exposure is historical
  coinglass: 20 * 60 * 1000,  // 20 minutes minimum - funding rates change every 8h
  defillama: 5 * 60 * 1000    // 5 minutes minimum - TVL changes slowly
};

/**
 * Calculate optimal polling interval for an API based on daily limits
 * @param {string} apiName - Name of the API
 * @param {number} priority - Priority level (1-5, 1=highest priority)
 * @returns {number} Optimal interval in milliseconds
 */
export function calculateOptimalInterval(apiName, priority = 3) {
  const dailyLimit = DAILY_LIMITS[apiName];
  const minInterval = MIN_INTERVALS[apiName];
  
  if (!dailyLimit) {
    console.warn(`No daily limit found for ${apiName}, using default interval`);
    return minInterval;
  }
  
  // Calculate safe daily usage (with safety margin)
  const safeDailyUsage = Math.floor(dailyLimit * SAFETY_MARGIN);
  
  // Calculate optimal interval based on daily usage
  // Higher priority = more frequent polling (but still very conservative)
  const priorityMultiplier = {
    1: 0.5,  // High priority: use 50% of safe limit
    2: 0.3,  // Medium-high priority: use 30% of safe limit
    3: 0.2,  // Medium priority: use 20% of safe limit
    4: 0.1,  // Low priority: use 10% of safe limit
    5: 0.05  // Very low priority: use 5% of safe limit
  };
  
  const effectiveUsage = Math.floor(safeDailyUsage * priorityMultiplier[priority]);
  
  // Calculate interval: 24 hours / effective usage
  const optimalInterval = (24 * 60 * 60 * 1000) / effectiveUsage;
  
  // Use the larger of optimal interval or minimum interval
  const finalInterval = Math.max(optimalInterval, minInterval);
  
  console.log(`${apiName} optimal interval: ${Math.round(finalInterval / 1000)}s (${Math.round(effectiveUsage)} calls/day, ${Math.round(safeDailyUsage)} safe limit)`);
  
  return Math.round(finalInterval);
}

/**
 * Get all optimal intervals for all APIs
 * @returns {Object} Object with API names as keys and intervals as values
 */
export function getAllOptimalIntervals() {
  return {
    // Critical data - medium priority (not high to avoid waste)
    morpho: calculateOptimalInterval('morpho', 3),      // Medium priority - utilization changes slowly
    coingecko: calculateOptimalInterval('coingecko', 3), // Medium priority - price changes but not constantly
    
    // Less critical data - very low priority
    etherscan: calculateOptimalInterval('etherscan', 5),  // Very low priority - historical data
    coinglass: calculateOptimalInterval('coinglass', 5),  // Very low priority - changes every 8h
    defillama: calculateOptimalInterval('defillama', 4)   // Low priority - TVL changes slowly
  };
}

/**
 * Validate that current intervals won't exceed daily limits
 * @param {Object} intervals - Current intervals object
 * @returns {Object} Validation results
 */
export function validateIntervals(intervals) {
  const results = {};
  
  Object.entries(intervals).forEach(([apiName, intervalMs]) => {
    const dailyLimit = DAILY_LIMITS[apiName];
    if (!dailyLimit) return;
    
    // Calculate expected daily usage
    const callsPerDay = (24 * 60 * 60 * 1000) / intervalMs;
    const percentageUsed = (callsPerDay / dailyLimit) * 100;
    
    results[apiName] = {
      intervalMs,
      callsPerDay: Math.round(callsPerDay),
      dailyLimit,
      percentageUsed: Math.round(percentageUsed * 100) / 100,
      isSafe: percentageUsed <= (SAFETY_MARGIN * 100),
      recommendation: percentageUsed > (SAFETY_MARGIN * 100) ? 'INCREASE_INTERVAL' : 'OK'
    };
  });
  
  return results;
}

/**
 * Get daily usage projections for current intervals
 * @param {Object} intervals - Current intervals object
 * @returns {Object} Daily usage projections
 */
export function getDailyUsageProjections(intervals) {
  const projections = {};
  
  Object.entries(intervals).forEach(([apiName, intervalMs]) => {
    const dailyLimit = DAILY_LIMITS[apiName];
    if (!dailyLimit) return;
    
    const callsPerDay = Math.round((24 * 60 * 60 * 1000) / intervalMs);
    const percentageUsed = Math.round(((callsPerDay / dailyLimit) * 100) * 100) / 100;
    
    projections[apiName] = {
      callsPerDay,
      dailyLimit,
      percentageUsed,
      remainingCalls: dailyLimit - callsPerDay,
      isWithinLimit: callsPerDay <= dailyLimit,
      safetyMargin: Math.round(((dailyLimit - callsPerDay) / dailyLimit) * 100)
    };
  });
  
  return projections;
}
